import { addDays, format } from 'date-fns';

/**
 * Converte uma string de data (YYYY-MM-DD) para Date local sem deslocamento de timezone.
 * Se a string já contém 'T' (timestamp completo), usa direto.
 * Se é apenas YYYY-MM-DD, adiciona T12:00:00 para forçar interpretação local.
 */
export function toLocalDate(dateStr: string | Date): Date {
  if (dateStr instanceof Date) return dateStr;
  if (dateStr.includes('T')) return new Date(dateStr);
  return new Date(dateStr + 'T12:00:00');
}

/**
 * Recebe "yyyy-MM-dd" e retorna o DIA SEGUINTE "yyyy-MM-dd".
 * Uso: .lt(coluna, exclusiveEndOfDay(endDate)) — inclui o dia final inteiro em colunas timestamptz.
 */
export function exclusiveEndOfDay(dateStr: string): string {
  return format(addDays(toLocalDate(dateStr), 1), 'yyyy-MM-dd');
}

/**
 * Converte parâmetro de data da URL (ex.: useFinancialFilters com endDate/startDate = 'all')
 * em Date local válida. Retorna undefined para '', 'all' ou strings inválidas.
 */
export function parseFinancialDateUrlParam(value: string | null | undefined): Date | undefined {
  if (value == null || value === '' || value === 'all') return undefined;
  const d = toLocalDate(value);
  return Number.isNaN(d.getTime()) ? undefined : d;
}

/**
 * Formata uma data de forma segura, retornando 'N/A' se inválida
 */
export const safeFormatDate = (
  date: string | Date | null | undefined,
  formatStr: string = 'dd/MM/yyyy'
): string => {
  if (!date) return 'N/A';
  if (typeof date === 'string' && date === 'all') return 'N/A';

  try {
    const dateObj = typeof date === 'string' ? toLocalDate(date) : date;
    
    // Verificar se é uma data válida
    if (isNaN(dateObj.getTime())) {
      return 'N/A';
    }
    
    return format(dateObj, formatStr);
  } catch (error) {
    console.error('Erro ao formatar data:', error, 'Data:', date);
    return 'N/A';
  }
};

// Mesmo padrão de useCounterReturns.ts / PickingKanbanView.tsx: yyyy-mm-dd
// no fuso America/Sao_Paulo via Intl, sem aritmética manual de offset.
const spDateFormatter = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' });

/**
 * "Hoje" em America/Sao_Paulo como 'YYYY-MM-DD'. Usar sempre que o alvo é
 * uma coluna DATE (grava ou filtra) e o valor pretendido é a data local
 * atual — substitui o anti-padrão `new Date().toISOString().split('T')[0]`,
 * que após 21:00 BRT já cai no dia seguinte (UTC-3).
 */
export function todayLocalStr(): string {
  return spDateFormatter.format(new Date());
}

/**
 * Converte um Date para 'YYYY-MM-DD' em America/Sao_Paulo. Usar ao
 * serializar um Date (de date-picker ou calculado) para uma coluna DATE —
 * substitui o anti-padrão `date.toISOString().split('T')[0]`, que desloca
 * o dia quando o horário local cai fora de 00:00–03:00 (span UTC-3).
 */
export function toDateOnlyLocal(d: Date): string {
  return spDateFormatter.format(d);
}
